import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { affablebpmIntegrationService } from "@services/affablebpmIntegration";
import { getCurrentUser } from "@utils/userUtils";
import {
  extractFacilityName,
  getSelectedFacilityFromSession,
} from "@utils/facilityUtils";

type FacilityResolver = () => any;

interface UseAffableConnectOptions {
  getFacilityData?: FacilityResolver;
  /**
   * Optional workflow type used when resolving DeptId from the
   * Cirules workflow‑mapping API for AffableBPM.
   * Examples: "Policy" (default), "Penalty", etc.
   */
  workflowType?: string;
}

export function useAffableConnect(options?: UseAffableConnectOptions) {
  const [isConnectingAffable, setIsConnectingAffable] = useState(false);

  const resolveFacilityData = useCallback(() => {
    if (typeof options?.getFacilityData === "function") {
      return options.getFacilityData();
    }
    return getSelectedFacilityFromSession();
  }, [options]);

  const getFacilityName = useCallback((): string => {
    const facility = resolveFacilityData();
    return extractFacilityName(facility || undefined);
  }, [resolveFacilityData]);

  const getFacilityOrgName = useCallback((): string => {
    const facility = resolveFacilityData();
    const orgName =
      (facility && typeof facility.org_name === "string" && facility.org_name) ||
      "";
    return orgName.trim();
  }, [resolveFacilityData]);

  const connectToAffableBPM = useCallback(async (overrideWorkflowType?: string) => {
    if (isConnectingAffable) return;

    const name = getFacilityName();
    if (!name) {
      toast.error("Facility name unavailable. Please select a facility first.");
      return;
    }

    const orgName = getFacilityOrgName();
    if (!orgName) {
      toast.error("Organization name not found for selected facility. Please contact support.");
      return;
    }

    setIsConnectingAffable(true);
    try {
      const currentUser = getCurrentUser();
      const payload = {
        UserName: currentUser?.email || "",
        NavigateURL: "",
        OrgName: orgName,
      };

      await affablebpmIntegrationService.ConnectWithAffableFLOW(payload, {
        target: "_blank",
        orgName,
        workflowType: overrideWorkflowType || options?.workflowType,
      });
    } catch (error) {
      console.error("Failed to connect to AffableFLOW:", error);
      const message =
        error instanceof Error
          ? error.message || "Failed to connect to AffableFLOW."
          : "Failed to connect to AffableFLOW.";
      toast.error(message);
    } finally {
      setIsConnectingAffable(false);
    }
  }, [getFacilityName, getFacilityOrgName, isConnectingAffable, options?.workflowType]);

  return {
    isConnectingAffable,
    getFacilityName,
    connectToAffableBPM,
  };
}

